import React from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import { Container, Row, Column } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs } from '../../components';
import { splitArray } from '../../components/utilities/functions';
import { isValidArray } from '../../components/utilities/validations';
import ShowroomsDirectory from '../../components/template/showroom-landing/directory';
import GoogleMap from '../../components/template/showroom-landing/google-map';
import DynamicComponent from '../../components/common/dynamic-component';
import BranchLinks from '../../components/template/branch-locations/branch-links';
import HeroSimple from '../../components/common/hero-simple';
import TopAccordion from '../../components/template/showroom-landing/top-accordion';

const ShowroomLanding = (props) => {
	const pageData = props.data.contentstackShowroomLanding;
	const branchData = props.data.branchData.nodes.sort((a, b) => a.state.province?.localeCompare(b.state.province));
	const usaList = splitArray([...new Set(branchData.map((item) => item.state.state_name))]);
	const canadaList = splitArray([...new Set(branchData.map((item) => item.state.province))]);
	const HeadingTag = pageData.data_type === 'branch' ? 'h1' : 'h2';

	return (
		<Layout id='template-ShowroomLanding'>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{isValidArray(pageData.page_hero) && (
				<HeroSimple
					className='[&_h1]:text-h2 [&_a]:no-underline [&_h1]:m-0 [&_p]:my-5 [&_p]:md:my-4 [&_p]:xl:my-6 [&_ul]:m-0'
					data={pageData.page_hero[0]}
				/>
			)}

			{isValidArray(pageData.hero_carousel?.content_sections) && <TopAccordion data={pageData.hero_carousel} />}

			{pageData.top_content && (
				<div className={`${pageData.data_type !== 'branch' ? 'text-center' : ''}`}>
					<Container>
						{pageData.top_content.heading && <HeadingTag className={`mb-6 md:mb-8`}>{pageData.top_content.heading}</HeadingTag>}
						{pageData.top_content.subheading && <p className='text-lg font-light md:text-2xl'>{pageData.top_content.subheading}</p>}
					</Container>
				</div>
			)}

			{pageData.data_type === 'showroom' && <GoogleMap type={pageData.data_type === 'lowes' ? 'LowesStores' : pageData.data_type === 'lumberyard' ? 'Stores' : 'Showrooms'} />}

			{pageData.data_type === 'showroom' && (
				<div className='py-6 xl:py-8'>
					<Container>
						{pageData.directory_heading && <h3 className='mb-4 md:mb-6'>{pageData.directory_heading}</h3>}

						{pageData.country_name && <p className='text-subtitle-2 mb-4 font-semibold text-gray-60'>{pageData.country_name}</p>}
						<ShowroomsDirectory region='usa' />

						{pageData.country_name_2 && <h3 className='text-subtitle-2 mb-4 mt-10 font-semibold text-gray-60'>{pageData.country_name_2}</h3>}
						<ShowroomsDirectory region='canada' />
					</Container>
				</div>
			)}

			{pageData.data_type === 'branch' && isValidArray(branchData) && (
				<Container className='pb-6 md:pb-8 xl:pb-12'>
					{pageData.country_name && <h2 className='mb-4 text-lg font-semibold uppercase tracking-3px md:mb-6 md:text-28px'>{pageData.country_name}</h2>}
					<Row>
						{usaList.map((col, index) => (
							<Column
								key={index}
								xs={12}
								lg={6}
								className='space-y-8'
							>
								{col.map((item, index) => {
									if (!item) return null;

									return (
										<div key={index}>
											<p className='mb-5 text-lg font-bold md:mb-6 md:text-2xl'>
												<Link
													to={branchData.find((branch) => branch.state.state_name === item)?.state_url}
													className='!leading-normal md:text-xl'
												>
													{branchData.find((branch) => branch.state.state_name === item)?.state.state_name}
												</Link>
											</p>

											{branchData
												.filter((branch) => branch.state.state_name === item)
												.sort((a, b) => a.formal_name.localeCompare(b.formal_name))
												.map((branch, index) => (
													<BranchLinks
														key={index}
														branch={branch}
														className={index > 0 ? 'mt-6' : null}
													/>
												))}
										</div>
									);
								})}
							</Column>
						))}
					</Row>

					{pageData.country_name_2 && <h2 className='mb-4 mt-10 text-lg font-semibold uppercase tracking-3px md:mb-6 md:mt-16 md:text-28px'>{pageData.country_name_2}</h2>}
					<Row>
						{canadaList.map((col, index) => (
							<Column
								key={index}
								xs={12}
								lg={6}
								className='space-y-8'
							>
								{col.map((item, index) => {
									if (!item) return null;

									return (
										<div key={index}>
											<p className='mb-5 text-lg font-bold md:mb-6 md:text-2xl'>
												<Link
													to={branchData.find((branch) => branch.state.province === item)?.state_url}
													className='!leading-normal md:text-xl'
												>
													{branchData.find((branch) => branch.state.province === item)?.state.province}
												</Link>
											</p>

											{branchData
												.filter((branch) => branch.state.province === item)
												.map((branch, index) => (
													<BranchLinks
														key={index}
														branch={branch}
														className={index > 0 ? 'mt-6' : null}
													/>
												))}
										</div>
									);
								})}
							</Column>
						))}
					</Row>
				</Container>
			)}

			{isValidArray(pageData.all_dynamic) &&
				pageData.all_dynamic.map((item, index) => (
					<DynamicComponent
						data={item}
						key={index}
					/>
				))}
		</Layout>
	);
};

ShowroomLanding.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default ShowroomLanding;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackShowroomLanding.seo}
	/>
);

export const query = graphql`
	query ($uid: String!) {
		contentstackShowroomLanding(id: { eq: $uid }) {
			url
			data_type
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
				back_pages {
					...CTA
				}
			}
			has_subnav
			hero_carousel {
				heading
				description
				content_sections {
					heading
					description
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					file {
						url
						gatsbyImageData(quality: 90)
					}
				}
			}
			top_content {
				heading
				subheading
			}
			country_name
			country_name_2
			directory_heading
			cta_ribbon {
				...CTARibbon
			}
			all_dynamic {
				heading
				dynamic {
					...Dynamic
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}

		branchData: allContentstackBranchData(sort: { state: { state_name: ASC } }) {
			nodes {
				state {
					state_name
					abbreviation
					province
				}
				state_url
				branch_retail
				additional_showrooms
				formal_name
				branch_url
			}
		}
	}
`;
